import { MenuItem, TextField } from "@mui/material"
import type { JSX } from "react"

import { typeOf, valueError, type PreShipLine } from "@crm-src/lib/pre-ship-api"

export interface PreShipValueInputProps {
  row: PreShipLine
  disabled: boolean
  onChange: (value: string) => void
  /** crm-api's verdict from the last save, shown until the value changes. */
  serverError?: string | undefined
}

/**
 * The VALUE cell of a pre-ship line, shaped by the line's type (2026-10-23):
 * a Yes/No or allowed-values select, a date picker, or a text box that says
 * under itself when what was typed does not fit. Blank is always fine.
 */
export function PreShipValueInput({ row, disabled, onChange, serverError }: PreShipValueInputProps): JSX.Element {
  const type = typeOf(row)
  const choices = row.choices ?? []
  const error = valueError(type, row.value, choices) ?? serverError ?? null
  const common = {
    size: "small" as const,
    fullWidth: true,
    disabled,
    value: row.value,
    error: Boolean(error),
    helperText: error ?? undefined,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
    inputProps: { "aria-label": `Value: ${row.label}`, "data-cy": "preship-row-value" },
  }

  if (type === "yes_no" || (type === "choice" && choices.length > 0)) {
    const options = type === "yes_no" ? ["Yes", "No"] : choices
    // A stored value no longer in the list stays visible (and marked) rather than silently blanked.
    const shown = row.value && !options.includes(row.value) ? [...options, row.value] : options
    return (
      <TextField {...common} select SelectProps={{ displayEmpty: true }}>
        <MenuItem value="">
          <em>Not answered</em>
        </MenuItem>
        {shown.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (type === "date") {
    return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />
  }

  const placeholder: Partial<Record<ReturnType<typeof typeOf>, string>> = {
    number: "0",
    email: "name@company.com",
    phone: "+1 555 010 0100",
    ip_address: "192.168.1.10",
  }
  return <TextField {...common} placeholder={placeholder[type] ?? "Value"} type={type === "email" ? "email" : "text"} />
}
